// ─────────────────────────────────────────────────────────────────────────────
// teachingFormat.js
// Converts raw teacher.teaching / teacher.classes+subjects into human-readable
// compact  → "1–8 (All) | 9–10 (PCM)"          (search cards)
// detailed → ["1–8 (All Subjects)", "9–10 (PCM)"] (profile page)
// ─────────────────────────────────────────────────────────────────────────────

const PRESCHOOL_ORDER = ["Nursery", "LKG", "UKG"];

// Extract the numeric class level from a class name string
// "Class 9"              → 9
// "Class 11 – Science"   → 11
// "Nursery"              → null
const getClassNum = (className) => {
  const m = className.match(/Class\s+(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
};

// Map a class + subjects to a short subject label
// Priority: class-name encoding (for 11-12) → explicit subjects → fallback
const getSubjectLabel = (className, subjects) => {
  // ── Senior secondary (11–12): stream is already in the class name ────────
  if (/Class\s+1[12]/i.test(className)) {
    if (className.includes("PCM"))      return "PCM";
    if (className.includes("PCB"))      return "PCB";
    if (className.includes("Commerce")) return "Commerce";
    if (className.includes("Arts"))     return "Arts";
    if (className.includes("Science"))  return "Science";
  }

  if (!subjects || subjects.length === 0) return "";

  // ── Explicit "All" marker (set by Dashboard "All Subjects" toggle) ────────
  if (subjects.includes("All")) return "All";

  // ── Common stream shortcuts ───────────────────────────────────────────────
  const lower = subjects.map(s => s.toLowerCase());
  const has   = (s) => lower.includes(s.toLowerCase());

  if (has("physics") && has("chemistry") && has("maths"))   return "PCM";
  if (has("physics") && has("chemistry") && has("biology")) return "PCB";

  // ── Fallback: first 3 subjects joined ────────────────────────────────────
  return subjects.slice(0, 3).join(", ");
};

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Array}  teaching       teacher.teaching  [{class, subjects}]
 * @param {Array}  legacyClasses  teacher.classes   (old flat array)
 * @param {Array}  legacySubjects teacher.subjects  (old flat array)
 * @returns {{ compact: string|null, detailed: string[] }}
 */
export const formatTeachingStructure = (teaching, legacyClasses, legacySubjects) => {
  try {
    // ── Step 1: Normalise to [{class, subjects}] ──────────────────────────
    let entries = [];

    if (teaching?.length > 0) {
      entries = teaching;
    } else if (legacyClasses?.length > 0) {
      // Old format: subjects are shared across all classes
      const subs = legacySubjects?.length > 0 ? legacySubjects : ["All"];
      entries = (Array.isArray(legacyClasses) ? legacyClasses : [legacyClasses])
        .map(c => ({ class: c, subjects: subs }));
    }

    if (!entries.length) return { compact: null, detailed: [] };

    // ── Step 2: Categorise entries ────────────────────────────────────────
    const preschool  = [];  // Nursery / LKG / UKG
    const numbered   = [];  // { num, label }
    const graduation = [];  // B.Tech etc.

    for (const entry of entries) {
      const cls = (entry.class || "").trim();
      if (!cls) continue;

      // Pre-school (no class number)
      if (PRESCHOOL_ORDER.includes(cls)) {
        preschool.push(cls);
        continue;
      }

      // Graduation (B.Tech, etc.)
      if (/b\.?tech/i.test(cls)) {
        graduation.push(cls);
        continue;
      }

      const num = getClassNum(cls);
      if (num === null) continue; // unknown class — skip gracefully

      const label = getSubjectLabel(cls, entry.subjects);
      numbered.push({ num, label });
    }

    // ── Step 3: Sort + de-duplicate numbered entries ──────────────────────
    numbered.sort((a, b) => a.num - b.num || a.label.localeCompare(b.label));

    const deduped = numbered.filter(
      (item, i) =>
        i === 0 ||
        !(item.num === numbered[i - 1].num && item.label === numbered[i - 1].label)
    );

    // ── Step 4: Group consecutive entries with the SAME label ─────────────
    // e.g. [{1,All},{2,All},{3,All}] → {min:1, max:3, label:"All"}
    const groups = [];
    for (const item of deduped) {
      const last = groups[groups.length - 1];
      if (last && last.label === item.label && item.num === last.max + 1) {
        last.max = item.num;                // extend existing range
      } else {
        groups.push({ min: item.num, max: item.num, label: item.label });
      }
    }

    // ── Step 5: Build text parts ──────────────────────────────────────────
    const parts = [];

    // Pre-school range
    if (preschool.length > 0) {
      const uniqueSorted = [...new Set(preschool)].sort(
        (a, b) => PRESCHOOL_ORDER.indexOf(a) - PRESCHOOL_ORDER.indexOf(b)
      );
      parts.push(
        uniqueSorted.length === 1
          ? uniqueSorted[0]
          : `${uniqueSorted[0]}–${uniqueSorted[uniqueSorted.length - 1]}`
      );
    }

    // Numbered groups
    for (const g of groups) {
      const range = g.min === g.max ? `${g.min}` : `${g.min}–${g.max}`;
      const tag   = g.label ? ` (${g.label})` : "";
      parts.push(range + tag);
    }

    // Graduation
    for (const g of graduation) {
      if (g.includes("CSE")) parts.push("B.Tech (CSE)");
      else parts.push(g);
    }

    if (!parts.length) return { compact: null, detailed: [] };

    // compact  → "(All)" stays as-is  → "1–8 (All) | 9–10 (PCM)"
    // detailed → "(All)" → "(All Subjects)"
    const detailed = parts.map(p => p.replace(" (All)", " (All Subjects)"));

    return {
      compact: parts.join(" | "),
      detailed,
    };
  } catch {
    // Never crash — caller gets null and falls back to original UI
    return { compact: null, detailed: [] };
  }
};
